import { useState, useEffect } from "react";

import { Check, ChevronDown } from "lucide-react";
import { Dropdown } from "antd";

import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

const LANGUAGES = {
    en: {
        name: "English",
        nativeName: "English",
        flag: "🇺🇸",
    },
    es: {
        name: "Spanish",
        nativeName: "Español",
        flag: "🇪🇸",
    },
};

const LanguageSelector = ({ className, variant }) => {
    const { i18n } = useTranslation();
    const [selectedLanguage, setSelectedLanguage] = useState(i18n.language);

    useEffect(() => {
        // Set initial language from i18n
        setSelectedLanguage(i18n.language);
    }, [i18n.language]);

    const applyLanguage = (language) => {
        setSelectedLanguage(language);
        i18n.changeLanguage(language);
        localStorage.setItem("userLanguage", language);
    };

    const handleDropdownMenuClick = ({ key }) => {
        applyLanguage(key);
    };

    const isLangActive = (lang) => selectedLanguage === lang || selectedLanguage?.startsWith(`${lang}-`);

    if (variant === "onboarding") {
        const langKey = selectedLanguage?.startsWith("es") ? "es" : "en";
        const current = LANGUAGES[langKey];

        const menuItems = Object.keys(LANGUAGES).map((lang) => ({
            key: lang,
            label: (
                <span className="flex items-center gap-2">
                    <span>{LANGUAGES[lang].flag}</span>
                    <span>{lang.toUpperCase()}</span>
                    {isLangActive(lang) ? <Check size={14} className="ml-auto text-[#5D5DF6]" /> : null}
                </span>
            ),
        }));

        return (
            <div className={`relative flex items-center ${className || ""}`}>
                <Dropdown menu={{ items: menuItems, onClick: handleDropdownMenuClick }} trigger={["click"]} placement="bottomRight">
                    <button
                        type="button"
                        className="flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-sm shadow-sm hover:bg-gray-50"
                        aria-label="Language">
                        <span aria-hidden>{current.flag}</span>
                        <span className="font-medium text-gray-800">{langKey === "en" ? "ENG" : "ESP"}</span>
                        <ChevronDown size={14} className="text-gray-500" aria-hidden />
                    </button>
                </Dropdown>
            </div>
        );
    }

    return (
        <div className={`relative flex items-center ${className || ""}`}>
            <div className="flex items-center gap-2 bg-white rounded-full px-2 py-1 border border-gray-200 shadow-sm">
                {Object.keys(LANGUAGES).map((lang) => (
                    <button
                        type="button"
                        key={lang}
                        className={`flex items-center gap-1 px-2 py-1 rounded-full ${
                            isLangActive(lang) ? "bg-[#5D5DF6] text-white" : "hover:bg-gray-100"
                        }`}
                        onClick={() => applyLanguage(lang)}
                        aria-label={`Switch to ${LANGUAGES[lang].name}`}>
                        <span className="text-sm">{LANGUAGES[lang].flag}</span>
                        <span className="text-sm">{lang.toUpperCase()}</span>
                        {isLangActive(lang) && <Check size={14} />}
                    </button>
                ))}
            </div>
        </div>
    );
};

LanguageSelector.propTypes = {
    className: PropTypes.string,
    variant: PropTypes.oneOf(["default", "onboarding"]),
};

LanguageSelector.defaultProps = {
    className: "",
    variant: "default",
};

export default LanguageSelector;
